import React, { useRef, useState } from 'react';
import { Box, useTheme, Button, Typography } from '@mui/material';
import { Settings } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import ImportExportMAIQ, { ImportExportMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/ImportExportMAIQ';
import { ImportExportTypes } from 'app/components/ReusableUIComponents/HighLevelComponents/ImportExportMAIQ/types';
import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';

interface DealerSettingsImportExportProps {
  selectedDealerId?: string;
  renderInAccordion?: boolean;
  accordionTitle?: string;
}

const ContentImportExport: React.FC<DealerSettingsImportExportProps> = ({
  selectedDealerId,
  renderInAccordion = false,
  accordionTitle = 'Content Import/Export',
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const importExportRef = useRef<ImportExportMAIQRef>(null);
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  // Configure the import/export for dealer settings
  const importExportConfig = ImportExportTypes.OFFERS(selectedDealerId);

  const handleSuccess = (result: any) => {
    console.log('Import/Export successful:', result);
    // You can add additional success handling here
  };

  const handleError = (error: any) => {
    console.error('Import/Export error:', error);
    // You can add additional error handling here
  };

  const handleImportComplete = (result: any) => {
    console.log('Import completed:', result);
    // Refresh page after successful import when modal closes
    if (result && result.success) {
      // Small delay to allow modal to close first
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  };

  const handleExportComplete = (result: any) => {
    console.log('Export completed:', result);
    // You can add specific export completion handling here
  };

  // Custom trigger button with dealer-specific styling
  const customTriggerButton = (
    <Button
      variant="contained"
      startIcon={<Settings />}
      disabled={!selectedDealerId}
      sx={{
        backgroundColor: isInAIAvatarLayout
          ? (theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white)
          : theme.palette.primary.main,
        color: isInAIAvatarLayout
          ? (theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black)
          : theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: isInAIAvatarLayout
            ? (theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[300])
            : theme.palette.primary.dark,
        },
        '&:disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
        textTransform: 'none',
        fontWeight: 600,
        px: 3,
        py: 1
      }}
    >
      Content Import/Export
    </Button>
  );

  if (renderInAccordion) {
    return (
      <Box sx={{ mt: 2 }}>
        <AccordionComponent
          elevation={0}
          expanded={accordionExpanded}
          onChange={(_, isExpanded) => setAccordionExpanded(isExpanded)}
          disabled={!selectedDealerId}
          sx={{
            '&::before': {
              display: 'none',
            },
            padding: '16px',
          }}
        >
          <AccordionSummaryComponent
            expandIcon={<ExpandMoreIcon />}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 15,
              backgroundColor: 'background.paper'
            }}
          >
            <Box sx={{ width: '100%', py: 0.5 }}>
            <Typography variant="h6" gutterBottom component="div">
              {accordionTitle}
            </Typography>
            </Box>
          </AccordionSummaryComponent>
          <AccordionDetailsComponent>
            <ImportExportMAIQ
              ref={importExportRef}
              type={importExportConfig}
              onSuccess={handleSuccess}
              onError={handleError}
              onImportComplete={handleImportComplete}
              onExportComplete={handleExportComplete}
              showImport={true}
              showExport={true}
              modalTitle="Offer Import/Export"
              disabled={!selectedDealerId}
              inline={true}
              showOverrideDealerIdToggle={true}
            />
          </AccordionDetailsComponent>
        </AccordionComponent>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ImportExportMAIQ
        ref={importExportRef}
        type={importExportConfig}
        onSuccess={handleSuccess}
        onError={handleError}
        onImportComplete={handleImportComplete}
        onExportComplete={handleExportComplete}
        showImport={true}
        showExport={true}
        modalTitle="Offer Import/Export"
        triggerButton={customTriggerButton}
        disabled={!selectedDealerId}
        showOverrideDealerIdToggle={true}
      />
    </Box>
  );
};

export default ContentImportExport;
